import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import type { FormControl } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import type { BackendAllErrorCodes } from '@freelancer/datastore';
import { ResponseData } from '@freelancer/freelancer-http';
import type { LoginError } from '@freelancer/login-signup';
import {
  LinkedInPixelTrackingEventConversionId,
  TiktokCustomEvents,
} from '@freelancer/tracking';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { LogoSize } from '@freelancer/ui/logo';
import { SpinnerBackgroundColor, SpinnerSize } from '@freelancer/ui/spinner';
import { RoleApi } from 'api-typings/common/common';
import { Subscription } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-account-type-form',
  template: `
    <app-form-logo
      [showBackButton]="true"
      [size]="logoSize"
      [backTrackingLabel]="'AccountTypeForm-Back'"
      (back)="back.emit()"
    ></app-form-logo>
    <form
      class="Form"
      [formGroup]="formGroup"
    >
      <h1
        class="text-mid"
        i18n="Signup account type selection heading"
      >
        Select account type
      </h1>
      <p class="text-left mb-mid">
        @if (subheaderText) {
          {{ subheaderText }}
        } @else {
          <ng-container i18n="Signup account type selection description">
            Don't worry, this can be changed later.
          </ng-container>
        }
      </p>
      <fl-radio-card
        class="RadioCard mb-small"
        flTrackingLabel="AccountTypeForm-FreelancerCard"
        flTrackingConversion="User Sign Up"
        [flTrackingExtraParams]="{ account_type: 'freelancer' }"
        [flTrackingPixelParams]="{
          linkedInEventData: {
            conversion_id:
              LinkedInPixelTrackingEventConversionId.FREELANCER_SIGN_UP,
          },
        }"
        [control]="formGroup.controls.accountType"
        [isSelected]="
          formGroup.controls.accountType.value === RoleApi.FREELANCER
        "
        [value]="RoleApi.FREELANCER"
      >
        <fl-grid [vAlign]="VerticalAlignment.VERTICAL_CENTER">
          <fl-col [col]="4">
            <div class="SpinnerOverlayContainer">
              @if (busy && formGroup.value.accountType === 'freelancer') {
                <fl-spinner
                  [overlay]="true"
                  flTrackingLabel="AccountTypeForm-SignupAsFreelancerSpinner"
                  [size]="SpinnerSize.MID"
                  [backgroundColor]="SpinnerBackgroundColor.INHERIT"
                ></fl-spinner>
              }
              <fl-picture
                i18n-alt="Alternative text"
                alt="Work illustration"
                [class.Invisible]="
                  busy && formGroup.value.accountType === 'freelancer'
                "
                [src]="'login-signup/work.svg'"
                [fullWidth]="true"
              ></fl-picture>
            </div>
          </fl-col>
          <fl-col [col]="8">
            <p class="text-small font-bold">
              @if (freelancerLabel) {
                {{ freelancerLabel }}
              } @else {
                <ng-container i18n="Signup account type freelancer label">
                  Earn money freelancing
                </ng-container>
              }
            </p>
          </fl-col>
        </fl-grid>
      </fl-radio-card>
      <fl-radio-card
        class="RadioCard mb-small"
        flTrackingLabel="AccountTypeForm-EmployerCard"
        flTrackingConversion="User Sign Up"
        [flTrackingExtraParams]="{ account_type: 'employer' }"
        [flTrackingPixelParams]="{
          tiktokEventName: TiktokCustomEvents.EMPLOYER_SIGN_UP,
        }"
        [control]="formGroup.controls.accountType"
        [isSelected]="formGroup.controls.accountType.value === RoleApi.EMPLOYER"
        [value]="RoleApi.EMPLOYER"
      >
        <fl-grid [vAlign]="VerticalAlignment.VERTICAL_CENTER">
          <fl-col [col]="4">
            <div class="SpinnerOverlayContainer">
              @if (busy && formGroup.value.accountType === 'employer') {
                <fl-spinner
                  [overlay]="true"
                  flTrackingLabel="AccountTypeForm-SignupAsEmployerSpinner"
                  [size]="SpinnerSize.MID"
                  [backgroundColor]="SpinnerBackgroundColor.INHERIT"
                ></fl-spinner>
              }
              <fl-picture
                i18n-alt="Alternative text"
                alt="Hire illustration"
                [class.Invisible]="
                  busy && formGroup.value.accountType === 'employer'
                "
                [src]="'login-signup/hire.svg'"
                [fullWidth]="true"
              ></fl-picture>
            </div>
          </fl-col>
          <fl-col [col]="8">
            <p class="text-small font-bold">
              @if (employerLabel) {
                {{ employerLabel }}
              } @else {
                <ng-container i18n="Signup account type employer label">
                  Hire a freelancer
                </ng-container>
              }
            </p>
          </fl-col>
        </fl-grid>
      </fl-radio-card>

      @if (response && response.status === 'error') {
        <app-login-signup-errors
          class="mb-small"
          [response]="response"
          (switchToLogin)="switchToLogin.emit()"
        ></app-login-signup-errors>
      }
    </form>
  `,
  styleUrls: ['./account-type-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountTypeFormComponent implements OnInit, OnDestroy {
  SpinnerSize = SpinnerSize;
  VerticalAlignment = VerticalAlignment;
  SpinnerBackgroundColor = SpinnerBackgroundColor;
  LinkedInPixelTrackingEventConversionId =
    LinkedInPixelTrackingEventConversionId;
  TiktokCustomEvents = TiktokCustomEvents;
  RoleApi = RoleApi;

  @Input() logoSize = LogoSize.MID;
  @Input() formGroup: FormGroup<{ accountType: FormControl<RoleApi> }>;
  @Input() response?: ResponseData<unknown, BackendAllErrorCodes | LoginError>;
  @Input() employerLabel?: string;
  @Input() freelancerLabel?: string;
  @Input() subheaderText?: string;
  @Input() busy = false;

  @Output() back = new EventEmitter();
  @Output() complete = new EventEmitter();
  @Output() switchToLogin = new EventEmitter();

  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(
      this.formGroup.valueChanges.subscribe(() => {
        if (this.response === null) {
          return;
        }
        this.complete.emit();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
